import { type ComponentType, lazy } from "react"

export interface Route {
  additionalProps?: Record<string, string>
  browserTitle: string
  component: ComponentType<Record<string, string>>
  key: string
  label: string
  language?: string
  mobile?: boolean
  route: string
}

interface BaseCreateConfig {
  additionalProps?: Record<string, string>
  component: ComponentType<Record<string, string>>
  key: string
  label: string
  mobile?: boolean
}

type CreateConfigConditional = BaseCreateConfig & { when: boolean }

export function createConfig(input: BaseCreateConfig): Route
export function createConfig(input: CreateConfigConditional): null | Route
export function createConfig(input: BaseCreateConfig & { when?: boolean }) {
  if (input?.when === false) {
    return null
  }
  // SAFETY: object literal matches Route shape
  return {
    additionalProps: input.additionalProps,
    browserTitle: input.label,
    component: input.component,
    key: input.key,
    label: input.label,
    mobile: input.mobile,
    route: `/${input.key}`,
  } as Route
}

const RELOAD_COOLDOWN_MS = 60_000
const RELOAD_STORAGE_KEY = "lazy-chunk-reload-timestamp"

// A stale chunk surfaces in several shapes, not just TypeError: Vercel's SPA
// rewrite serves a missing chunk as 200+HTML, which the browser reports as
// `Unexpected token '<'` (SyntaxError); Safari says `Importing a module script
// failed`; old Vite preload helpers threw `Failed to load dynamic import`.
const CHUNK_LOAD_ERROR_MESSAGE =
  /Failed to fetch dynamically imported module|error loading dynamically imported|Importing a module script failed|Failed to load dynamic import|Unexpected token/i

export function lazyRoute<RouteModule extends Record<string, unknown>>(
  loader: ()=> Promise<RouteModule>,
  name: string,
) {
  return lazy(async ()=> {
    try {
      // SAFETY: each route module's named export is the expected component
      const mod = await loader()
      return { default: mod[name] as ComponentType<Record<string, string>> }
    } catch (error) {
      if (isChunkLoadError(error) && shouldReload()) {
        markReloaded()
        window.location.reload()
      }
      throw error
    }
  })
}

function isChunkLoadError(error: unknown): boolean {
  if (error instanceof TypeError) return true
  // eslint-disable-next-line anti-slop/no-runtime-typeof -- boundary type guard on union input
  if (typeof error === "string") {
    return CHUNK_LOAD_ERROR_MESSAGE.test(error)
  }
  return error instanceof Error && CHUNK_LOAD_ERROR_MESSAGE.test(error.message)
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_STORAGE_KEY, String(Date.now()))
  } catch {
    /* sessionStorage unavailable — proceed without throttling */
  }
}

function shouldReload(): boolean {
  try {
    const last = sessionStorage.getItem(RELOAD_STORAGE_KEY)
    if (!last) return true
    return Date.now() - Number(last) > RELOAD_COOLDOWN_MS
  } catch {
    return true
  }
}
